import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function SuperAdmin() {
  const { apiFetch, user: currentUser } = useAuth();
  const [associations, setAssociations] = useState([]);
  const [stats, setStats] = useState({ associations: 0, users: 0, members: 0 });
  const [showNewAssoc, setShowNewAssoc] = useState(false);
  const [showEditAssoc, setShowEditAssoc] = useState(null);
  const [showDeleteAssoc, setShowDeleteAssoc] = useState(null);
  const [showNewUser, setShowNewUser] = useState(null);
  const [showNewSuperAdmin, setShowNewSuperAdmin] = useState(false);
  const [assocForm, setAssocForm] = useState({ name: '', cvr: '', address: '', postal_code: '', city: '', phone: '', email: '', account_type: 'forening' });
  const [editAssocForm, setEditAssocForm] = useState({ name: '', cvr: '', address: '', postal_code: '', city: '', phone: '', email: '', account_type: 'forening' });
  const [userForm, setUserForm] = useState({ username: '', password: '', name: '', role: 'admin' });
  const [superAdminForm, setSuperAdminForm] = useState({ username: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [assocUsers, setAssocUsers] = useState({});
  const [superAdminUsers, setSuperAdminUsers] = useState([]);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showEditUser, setShowEditUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ username: '', name: '', role: 'admin', password: '' });

  const load = async () => {
    try {
      const [assocs, s, saUsers] = await Promise.all([
        apiFetch('/api/superadmin/associations'),
        apiFetch('/api/superadmin/stats'),
        apiFetch('/api/superadmin/superadmin-users')
      ]);
      setAssociations(assocs);
      setStats(s);
      setSuperAdminUsers(saUsers);
    } catch (e) { /* */ }
  };

  useEffect(() => { load(); }, []);

  const loadAssocUsers = async (assocId) => {
    try {
      const users = await apiFetch(`/api/superadmin/associations/${assocId}/users`);
      setAssocUsers(prev => ({ ...prev, [assocId]: users }));
    } catch (e) { /* */ }
  };

  const createAssociation = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    try {
      await apiFetch('/api/superadmin/associations', { method: 'POST', body: assocForm });
      setShowNewAssoc(false);
      setAssocForm({ name: '', cvr: '', address: '', postal_code: '', city: '', phone: '', email: '', account_type: 'forening' });
      setMessage('Forening oprettet.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditAssoc = (assoc) => {
    setEditAssocForm({
      name: assoc.name || '',
      cvr: assoc.cvr || '',
      address: assoc.address || '',
      postal_code: assoc.postal_code || '',
      city: assoc.city || '',
      phone: assoc.phone || '',
      email: assoc.email || '',
      account_type: assoc.account_type || 'forening'
    });
    setShowEditAssoc(assoc);
    setError(''); setMessage('');
  };

  const updateAssociation = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    try {
      await apiFetch(`/api/superadmin/associations/${showEditAssoc.id}`, { method: 'PUT', body: editAssocForm });
      setShowEditAssoc(null);
      setMessage('Forening opdateret.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const openDeleteAssoc = (assoc) => {
    setShowDeleteAssoc(assoc);
    setDeleteConfirmText('');
    setError(''); setMessage('');
  };

  const deleteAssociation = async () => {
    setError(''); setMessage('');
    try {
      await apiFetch(`/api/superadmin/associations/${showDeleteAssoc.id}`, { method: 'DELETE' });
      setShowDeleteAssoc(null);
      setDeleteConfirmText('');
      setMessage('Forening slettet.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    try {
      await apiFetch(`/api/superadmin/associations/${showNewUser}/users`, { method: 'POST', body: userForm });
      setShowNewUser(null);
      setUserForm({ username: '', password: '', name: '', role: 'admin' });
      setMessage('Bruger oprettet.');
      loadAssocUsers(showNewUser);
    } catch (err) {
      setError(err.message);
    }
  };

  const createSuperAdmin = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    try {
      await apiFetch('/api/superadmin/superadmin-users', { method: 'POST', body: superAdminForm });
      setShowNewSuperAdmin(false);
      setSuperAdminForm({ username: '', password: '', name: '' });
      setMessage('Superadmin bruger oprettet.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditUser = (user, associationId, isSuperAdmin = false) => {
    setEditUserForm({
      username: user.username || '',
      name: user.name || '',
      role: user.role || 'admin',
      password: ''
    });
    setShowEditUser({ user, associationId, isSuperAdmin });
    setError(''); setMessage('');
  };

  const updateUser = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    const body = { ...editUserForm };
    if (!body.password) delete body.password;
    try {
      await apiFetch(`/api/superadmin/users/${showEditUser.user.id}`, { method: 'PUT', body });
      const assocId = showEditUser.associationId;
      setShowEditUser(null);
      setMessage('Bruger opdateret.');
      load();
      if (assocId) loadAssocUsers(assocId);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Slet denne bruger?')) return;
    try {
      await apiFetch(`/api/superadmin/users/${userId}`, { method: 'DELETE' });
      setMessage('Bruger slettet.');
      load();
      Object.keys(assocUsers).forEach(id => loadAssocUsers(id));
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteSuperAdminUser = async (userId) => {
    if (userId === currentUser?.id) {
      alert('Du kan ikke slette din egen bruger.');
      return;
    }
    if (!window.confirm('Er du sikker på at du vil slette denne superadmin bruger?')) return;
    try {
      await apiFetch(`/api/superadmin/users/${userId}`, { method: 'DELETE' });
      setMessage('Superadmin bruger slettet.');
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const [expanded, setExpanded] = useState(null);
  const toggleExpand = (assocId) => {
    if (expanded === assocId) {
      setExpanded(null);
    } else {
      setExpanded(assocId);
      if (!assocUsers[assocId]) loadAssocUsers(assocId);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Superadmin</h1>
        <button className="btn btn-primary" onClick={() => setShowNewAssoc(true)}>+ Ny forening</button>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card-grid">
        <div className="card">
          <div className="card-header">Foreninger</div>
          <div className="card-value">{stats.associations}</div>
        </div>
        <div className="card">
          <div className="card-header">Brugere</div>
          <div className="card-value">{stats.users}</div>
        </div>
        <div className="card">
          <div className="card-header">Medlemmer total</div>
          <div className="card-value">{stats.members}</div>
        </div>
      </div>

      {/* Superadmin brugere */}
      <h2 style={{fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem'}}>Superadmin brugere</h2>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Brugernavn</th>
              <th>Navn</th>
              <th>Oprettet</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {superAdminUsers.map(u => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td>{u.name}</td>
                <td style={{fontSize: '0.85rem', color: 'var(--gray-500)'}}>
                  {u.created_at ? new Date(u.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                </td>
                <td className="text-right" style={{whiteSpace: 'nowrap'}}>
                  <button className="btn btn-secondary btn-sm" style={{marginRight: '0.25rem'}} onClick={() => openEditUser(u, null, true)}>Rediger</button>
                  {u.id === currentUser?.id ? (
                    <span className="badge badge-active">Dig</span>
                  ) : (
                    <button className="btn btn-danger btn-sm" onClick={() => deleteSuperAdminUser(u.id)}>Slet</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{marginTop: '0.75rem'}}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNewSuperAdmin(true)}>+ Ny superadmin</button>
        </div>
      </div>

      {/* Foreninger */}
      <h2 style={{fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '0.75rem'}}>Foreninger</h2>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Navn</th>
              <th>Type</th>
              <th>CVR</th>
              <th>By</th>
              <th>Brugere</th>
              <th>Medlemmer</th>
              <th>Oprettet</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {associations.map(a => (
              <React.Fragment key={a.id}>
                <tr style={{cursor: 'pointer'}} onClick={() => toggleExpand(a.id)}>
                  <td><strong>{a.name}</strong></td>
                  <td><span className={`badge ${a.account_type === 'holding' ? '' : 'badge-active'}`}>{a.account_type === 'holding' ? 'Holding' : 'Forening'}</span></td>
                  <td>{a.cvr || '-'}</td>
                  <td>{a.city || '-'}</td>
                  <td>{a.user_count}</td>
                  <td>{a.member_count}</td>
                  <td>{a.created_at ? new Date(a.created_at).toLocaleDateString('da-DK') : '-'}</td>
                  <td className="text-right" style={{whiteSpace: 'nowrap'}}>
                    <button className="btn btn-secondary btn-sm" style={{marginRight: '0.25rem'}} onClick={(e) => { e.stopPropagation(); openEditAssoc(a); }}>
                      Rediger
                    </button>
                    <button className="btn btn-primary btn-sm" style={{marginRight: '0.25rem'}} onClick={(e) => { e.stopPropagation(); setShowNewUser(a.id); }}>
                      + Bruger
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); openDeleteAssoc(a); }}>
                      Slet
                    </button>
                  </td>
                </tr>
                {expanded === a.id && (
                  <tr>
                    <td colSpan="8" style={{background: 'var(--gray-50)', padding: '1rem'}}>
                      <strong style={{fontSize: '0.85rem'}}>Brugere i {a.name}:</strong>
                      {assocUsers[a.id] ? (
                        assocUsers[a.id].length === 0 ? (
                          <p style={{color: 'var(--gray-500)', fontSize: '0.85rem', marginTop: '0.5rem'}}>Ingen brugere endnu.</p>
                        ) : (
                          <table style={{marginTop: '0.5rem'}}>
                            <thead>
                              <tr>
                                <th>Brugernavn</th>
                                <th>Navn</th>
                                <th>Rolle</th>
                                <th>Oprettet</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {assocUsers[a.id].map(u => (
                                <tr key={u.id}>
                                  <td>{u.username}</td>
                                  <td>{u.name}</td>
                                  <td>
                                    <span className={`badge ${u.role === 'admin' ? 'badge-active' : ''}`}>
                                      {u.role === 'admin' ? 'Administrator' : 'Bruger'}
                                    </span>
                                  </td>
                                  <td style={{fontSize: '0.85rem', color: 'var(--gray-500)'}}>
                                    {u.created_at ? new Date(u.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </td>
                                  <td className="text-right" style={{whiteSpace: 'nowrap'}}>
                                    <button className="btn btn-secondary btn-sm" style={{marginRight: '0.25rem'}} onClick={() => openEditUser(u, a.id)}>Rediger</button>
                                    <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u.id)}>Slet</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      ) : (
                        <p style={{color: 'var(--gray-500)', fontSize: '0.85rem'}}>Indlæser...</p>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal: Ny forening */}
      {showNewAssoc && (
        <div className="modal-overlay" onClick={() => setShowNewAssoc(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Nyt regnskab</h2>
            <form onSubmit={createAssociation}>
              <div className="form-group">
                <label>Regnskabstype</label>
                <select className="form-control" value={assocForm.account_type}
                  onChange={e => setAssocForm({...assocForm, account_type: e.target.value})}>
                  <option value="forening">Foreningsregnskab</option>
                  <option value="holding">Holdingregnskab</option>
                </select>
              </div>
              <div className="form-group">
                <label>{assocForm.account_type === 'holding' ? 'Selskabsnavn' : 'Foreningsnavn'}</label>
                <input className="form-control" value={assocForm.name}
                  onChange={e => setAssocForm({...assocForm, name: e.target.value})} required autoFocus />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>CVR-nr.</label>
                  <input className="form-control" value={assocForm.cvr}
                    onChange={e => setAssocForm({...assocForm, cvr: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" className="form-control" value={assocForm.email}
                    onChange={e => setAssocForm({...assocForm, email: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input className="form-control" value={assocForm.address}
                  onChange={e => setAssocForm({...assocForm, address: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Postnr.</label>
                  <input className="form-control" value={assocForm.postal_code}
                    onChange={e => setAssocForm({...assocForm, postal_code: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>By</label>
                  <input className="form-control" value={assocForm.city}
                    onChange={e => setAssocForm({...assocForm, city: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input className="form-control" value={assocForm.phone}
                  onChange={e => setAssocForm({...assocForm, phone: e.target.value})} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewAssoc(false)}>Annuller</button>
                <button type="submit" className="btn btn-primary">Opret forening</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Rediger forening */}
      {showEditAssoc && (
        <div className="modal-overlay" onClick={() => setShowEditAssoc(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Rediger regnskab</h2>
            <form onSubmit={updateAssociation}>
              <div className="form-group">
                <label>Regnskabstype</label>
                <select className="form-control" value={editAssocForm.account_type}
                  onChange={e => setEditAssocForm({...editAssocForm, account_type: e.target.value})}>
                  <option value="forening">Foreningsregnskab</option>
                  <option value="holding">Holdingregnskab</option>
                </select>
              </div>
              <div className="form-group">
                <label>{editAssocForm.account_type === 'holding' ? 'Selskabsnavn' : 'Foreningsnavn'}</label>
                <input className="form-control" value={editAssocForm.name}
                  onChange={e => setEditAssocForm({...editAssocForm, name: e.target.value})} required autoFocus />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>CVR-nr.</label>
                  <input className="form-control" value={editAssocForm.cvr}
                    onChange={e => setEditAssocForm({...editAssocForm, cvr: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" className="form-control" value={editAssocForm.email}
                    onChange={e => setEditAssocForm({...editAssocForm, email: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input className="form-control" value={editAssocForm.address}
                  onChange={e => setEditAssocForm({...editAssocForm, address: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Postnr.</label>
                  <input className="form-control" value={editAssocForm.postal_code}
                    onChange={e => setEditAssocForm({...editAssocForm, postal_code: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>By</label>
                  <input className="form-control" value={editAssocForm.city}
                    onChange={e => setEditAssocForm({...editAssocForm, city: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input className="form-control" value={editAssocForm.phone}
                  onChange={e => setEditAssocForm({...editAssocForm, phone: e.target.value})} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditAssoc(null)}>Annuller</button>
                <button type="submit" className="btn btn-primary">Gem ændringer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Slet forening (advarsel) */}
      {showDeleteAssoc && (
        <div className="modal-overlay" onClick={() => setShowDeleteAssoc(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{color: 'var(--danger)'}}>Slet forening</h2>
            <div style={{background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '1rem', marginBottom: '1rem'}}>
              <p style={{color: '#991b1b', fontWeight: 600, marginBottom: '0.5rem'}}>
                Advarsel: Denne handling kan ikke fortrydes!
              </p>
              <p style={{color: '#991b1b', fontSize: '0.9rem', marginBottom: '0.5rem'}}>
                Du er ved at slette foreningen <strong>{showDeleteAssoc.name}</strong>.
              </p>
              {(showDeleteAssoc.user_count > 0 || showDeleteAssoc.member_count > 0) && (
                <p style={{color: '#991b1b', fontSize: '0.9rem', marginBottom: '0.5rem'}}>
                  Foreningen har {showDeleteAssoc.user_count > 0 && <strong>{showDeleteAssoc.user_count} bruger(e)</strong>}
                  {showDeleteAssoc.user_count > 0 && showDeleteAssoc.member_count > 0 && ' og '}
                  {showDeleteAssoc.member_count > 0 && <strong>{showDeleteAssoc.member_count} medlem(mer)</strong>}.
                  {showDeleteAssoc.user_count > 0 && ' Brugerne skal slettes først.'}
                </p>
              )}
              <p style={{color: '#991b1b', fontSize: '0.85rem'}}>
                Skriv foreningens navn for at bekræfte sletning:
              </p>
            </div>
            <div className="form-group">
              <input
                className="form-control"
                placeholder={showDeleteAssoc.name}
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteAssoc(null)}>Annuller</button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleteConfirmText !== showDeleteAssoc.name}
                onClick={deleteAssociation}
              >
                Slet forening permanent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Ny bruger til forening */}
      {showNewUser && (
        <div className="modal-overlay" onClick={() => setShowNewUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Ny bruger</h2>
            <p style={{fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '1rem'}}>
              Tilføjes til: {associations.find(a => a.id === showNewUser)?.name}
            </p>
            <form onSubmit={createUser}>
              <div className="form-row">
                <div className="form-group">
                  <label>Brugernavn (email)</label>
                  <input type="email" className="form-control" value={userForm.username}
                    onChange={e => setUserForm({...userForm, username: e.target.value})} required autoFocus />
                </div>
                <div className="form-group">
                  <label>Adgangskode</label>
                  <input type="password" className="form-control" value={userForm.password}
                    onChange={e => setUserForm({...userForm, password: e.target.value})} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Fulde navn</label>
                  <input className="form-control" value={userForm.name}
                    onChange={e => setUserForm({...userForm, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Rolle</label>
                  <select className="form-control" value={userForm.role}
                    onChange={e => setUserForm({...userForm, role: e.target.value})}>
                    <option value="admin">Administrator</option>
                    <option value="user">Bruger</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewUser(null)}>Annuller</button>
                <button type="submit" className="btn btn-primary">Opret bruger</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Ny superadmin bruger */}
      {showNewSuperAdmin && (
        <div className="modal-overlay" onClick={() => setShowNewSuperAdmin(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Ny superadmin bruger</h2>
            <p style={{fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '1rem'}}>
              Superadmin brugere har adgang til alle foreninger og systemindstillinger.
            </p>
            <form onSubmit={createSuperAdmin}>
              <div className="form-group">
                <label>Brugernavn (email)</label>
                <input type="email" className="form-control" value={superAdminForm.username}
                  onChange={e => setSuperAdminForm({...superAdminForm, username: e.target.value})} required autoFocus />
              </div>
              <div className="form-group">
                <label>Fulde navn</label>
                <input className="form-control" value={superAdminForm.name}
                  onChange={e => setSuperAdminForm({...superAdminForm, name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Adgangskode</label>
                <input type="password" className="form-control" value={superAdminForm.password}
                  onChange={e => setSuperAdminForm({...superAdminForm, password: e.target.value})} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewSuperAdmin(false)}>Annuller</button>
                <button type="submit" className="btn btn-primary">Opret superadmin</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Rediger bruger */}
      {showEditUser && (
        <div className="modal-overlay" onClick={() => setShowEditUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Rediger bruger</h2>
            <p style={{fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '1rem'}}>
              {showEditUser.isSuperAdmin ? 'Superadmin bruger' : `Bruger i: ${associations.find(a => a.id === showEditUser.associationId)?.name || ''}`}
            </p>
            <form onSubmit={updateUser}>
              <div className="form-group">
                <label>Brugernavn (email)</label>
                <input type="email" className="form-control" value={editUserForm.username}
                  onChange={e => setEditUserForm({...editUserForm, username: e.target.value})} required autoFocus />
              </div>
              <div className="form-group">
                <label>Fulde navn</label>
                <input className="form-control" value={editUserForm.name}
                  onChange={e => setEditUserForm({...editUserForm, name: e.target.value})} required />
              </div>
              {!showEditUser.isSuperAdmin && (
                <div className="form-group">
                  <label>Rolle</label>
                  <select className="form-control" value={editUserForm.role}
                    onChange={e => setEditUserForm({...editUserForm, role: e.target.value})}>
                    <option value="admin">Administrator</option>
                    <option value="user">Bruger</option>
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Ny adgangskode <span style={{fontWeight: 400, color: 'var(--gray-500)'}}>(lad stå tom for at beholde nuværende)</span></label>
                <input type="password" className="form-control" value={editUserForm.password}
                  onChange={e => setEditUserForm({...editUserForm, password: e.target.value})}
                  placeholder="Udfyld kun for at ændre" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditUser(null)}>Annuller</button>
                <button type="submit" className="btn btn-primary">Gem ændringer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
